/**
 * Safe ID & Family Emergency Card (§25) — parent view.
 *
 * Per child: an opt-in emergency profile (every visible field is a
 * deliberate toggle — privacy by default), the scannable QR token to print
 * or stick on a backpack, and the log of scans + finder messages relayed
 * through the public /found page (no personal contact info is ever exposed).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { QrCode, RefreshCw, ScanLine, MessageSquare, ExternalLink, MapPin } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { usersApi } from '@/api/users';
import { safeIdApi, type EmergencyProfile } from '@/api/safeId';
import { ChildPicker } from './ChildPicker';
import { PageHeader } from './PageHeader';

export function SafeIdPage() {
  const { t } = useTranslation();
  const children = useQuery({ queryKey: ['children'], queryFn: () => usersApi.listChildren() });
  const [childId, setChildId] = useState<string | null>(null);

  return (
    <div className="px-6 py-8 md:px-10 md:py-10">
      <PageHeader
        title={t('pages.safe_id.title')}
        icon={QrCode}
        tint="amber"
        description={t('pages.safe_id.description')}
      />
      <FinderMessages />
      <div className="mt-6">
        <ChildPicker children={children.data ?? []} selectedId={childId} onSelect={setChildId} />
      </div>
      {childId && (
        <div className="mt-6 grid gap-4 lg:grid-cols-[1fr_20rem]">
          <ProfileEditor childId={childId} />
          <TokenCard childId={childId} />
        </div>
      )}
    </div>
  );
}

const FIELDS: { key: keyof EmergencyProfile; label: string; show?: keyof EmergencyProfile; multiline?: boolean }[] = [
  { key: 'first_name', label: 'First name' },
  { key: 'allergies', label: 'Allergies', show: 'show_allergies' },
  { key: 'medical_notes', label: 'Medical notes', show: 'show_medical', multiline: true },
  { key: 'blood_type', label: 'Blood type', show: 'show_blood_type' },
  { key: 'school_name', label: 'School', show: 'show_school' },
  { key: 'languages', label: 'Languages spoken' },
  { key: 'what_to_do', label: 'What to do if found', multiline: true },
  { key: 'emergency_message', label: 'Message to the finder', multiline: true },
];

function ProfileEditor({ childId }: { childId: string }) {
  const queryClient = useQueryClient();
  const profile = useQuery({ queryKey: ['safeid', 'profile', childId], queryFn: () => safeIdApi.profile(childId) });
  const [draft, setDraft] = useState<Partial<EmergencyProfile>>({});
  const p = { ...profile.data, ...draft } as EmergencyProfile;

  const save = useMutation({
    mutationFn: (body: Partial<EmergencyProfile>) => safeIdApi.updateProfile(childId, body),
    onSuccess: () => {
      toast.success('Saved.');
      setDraft({});
      void queryClient.invalidateQueries({ queryKey: ['safeid', 'profile', childId] });
    },
    onError: () => toast.error('Save failed.'),
  });

  if (!profile.data)
    return (
      <Card>
        <CardContent className="space-y-3 p-6">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-2/3" />
        </CardContent>
      </Card>
    );

  const set = (k: keyof EmergencyProfile, v: unknown) => setDraft((d) => ({ ...d, [k]: v }));

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Emergency profile</CardTitle>
          <div className="flex items-center gap-2">
            <span className="text-xs text-[color:var(--kz-color-ink-muted)]">Active</span>
            <Switch checked={p.enabled} onCheckedChange={(v) => save.mutate({ enabled: v })} />
          </div>
        </div>
        <CardDescription>Toggle which fields a finder can see. Everything is hidden until you turn it on.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {FIELDS.map((f) => (
          <div key={f.key} className="grid gap-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor={`f-${f.key}`}>{f.label}</Label>
              {f.show && (
                <label className="flex items-center gap-1.5 text-xs text-[color:var(--kz-color-ink-muted)]">
                  visible
                  <Switch checked={Boolean(p[f.show])} onCheckedChange={(v) => save.mutate({ [f.show!]: v })} />
                </label>
              )}
            </div>
            {f.multiline ? (
              <textarea
                id={`f-${f.key}`}
                className="min-h-16 w-full rounded-md border border-[color:var(--kz-color-border-subtle)] bg-transparent p-2 text-sm"
                value={(p[f.key] as string) ?? ''}
                onChange={(e) => set(f.key, e.target.value)}
              />
            ) : (
              <Input
                id={`f-${f.key}`}
                value={(p[f.key] as string) ?? ''}
                onChange={(e) => set(f.key, e.target.value)}
              />
            )}
          </div>
        ))}
        <div className="flex items-center justify-between rounded-md border border-[color:var(--kz-color-border-subtle)] p-3">
          <div>
            <p className="text-sm font-medium">Allow finder to share location</p>
            <p className="text-xs text-[color:var(--kz-color-ink-muted)]">One-shot, ~100m accuracy, finder-consented.</p>
          </div>
          <Switch checked={p.allow_location_share} onCheckedChange={(v) => save.mutate({ allow_location_share: v })} />
        </div>
        <Button onClick={() => save.mutate(draft)} disabled={Object.keys(draft).length === 0 || save.isPending}>
          {save.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </CardContent>
    </Card>
  );
}

function TokenCard({ childId }: { childId: string }) {
  const queryClient = useQueryClient();
  const token = useQuery({ queryKey: ['safeid', 'token', childId], queryFn: () => safeIdApi.token(childId) });
  const scans = useQuery({ queryKey: ['safeid', 'scans', childId], queryFn: () => safeIdApi.scans(childId) });
  // Finder messages live in a separate table; merge them into the scans
  // timeline so the hover popup can surface the message + contact that
  // came with a scan. (Safe ID stores them separately, unlike Pet Guard.)
  const messages = useQuery({ queryKey: ['safeid', 'messages'], queryFn: () => safeIdApi.messages() });
  type HistoryRow = {
    id: string;
    ts: string;
    lat: number | null;
    lng: number | null;
    message?: string;
    finder_contact?: string;
    unread?: boolean;
  };
  const history: HistoryRow[] = (() => {
    const scanRows: HistoryRow[] = (scans.data ?? []).map((s) => ({
      id: `scan:${s.id}`,
      ts: s.scanned_at,
      lat: s.approx_lat,
      lng: s.approx_lng,
    }));
    const msgRows: HistoryRow[] = (messages.data ?? [])
      .filter((m) => m.child_id === childId)
      .map((m) => ({
        id: `msg:${m.id}`,
        ts: m.created_at,
        lat: m.approx_lat,
        lng: m.approx_lng,
        message: m.body,
        finder_contact: m.finder_contact ?? undefined,
        unread: !m.read_at,
      }));
    // A scan and its accompanying message land within seconds of each
    // other; suppress the bare scan row when a message exists nearby so
    // the timeline doesn't show two entries for the same event.
    const msgTimes = msgRows.map((m) => Date.parse(m.ts));
    const dedupedScans = scanRows.filter((s) => {
      const t = Date.parse(s.ts);
      return !msgTimes.some((mt) => Math.abs(mt - t) < 5000);
    });
    return [...dedupedScans, ...msgRows]
      .sort((a, b) => Date.parse(b.ts) - Date.parse(a.ts))
      .slice(0, 6);
  })();
  const rotate = useMutation({
    mutationFn: () => safeIdApi.rotate(childId),
    onSuccess: () => {
      toast.success('New code generated — the old one no longer works.');
      void queryClient.invalidateQueries({ queryKey: ['safeid', 'token', childId] });
    },
  });
  const t = token.data;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base"><QrCode className="size-4" /> Safe ID tag</CardTitle>
        <CardDescription>Print it or stick it on a backpack. Scanning opens the emergency page.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {t && (
          <>
            <div className="kz-qr-plate grid place-items-center rounded-card border border-[color:var(--kz-color-border-subtle)] bg-white p-4">
              <img src={safeIdApi.qrUrl(t.token)} alt="Safe ID QR code" className="size-44" />
            </div>
            <div className="flex items-center gap-2">
              <Input value={t.url} readOnly className="font-mono text-xs" />
              <Button variant="outline" size="icon" onClick={() => { void navigator.clipboard.writeText(t.url); toast.success('Link copied'); }} aria-label="Copy">
                <ExternalLink className="size-4" />
              </Button>
            </div>
            <Button variant="ghost" size="sm" onClick={() => rotate.mutate()} disabled={rotate.isPending}>
              <RefreshCw className="mr-1 size-3.5" /> Generate a new code
            </Button>
          </>
        )}
        <div className="border-t border-[color:var(--kz-color-border-subtle)] pt-3">
          <p className="flex items-center gap-1.5 text-sm font-medium"><ScanLine className="size-4" /> Recent scans</p>
          {history.length === 0 ? (
            <p className="mt-1 text-xs text-[color:var(--kz-color-ink-muted)]">No scans yet.</p>
          ) : (
            <ul className="mt-2 space-y-1">
              {history.map((h) => {
                const hasCoords = h.lat != null && h.lng != null;
                const mapHref = hasCoords
                  ? `/map?lat=${h.lat}&lng=${h.lng}&focus=${childId}&pin_label=!&pin_color=%23f43f5e`
                  : null;
                return (
                  <li
                    key={h.id}
                    className="rounded-md border border-[color:var(--kz-color-border-subtle)]/40 bg-[color:var(--kz-color-surface-card)] px-2.5 py-1.5 text-xs"
                  >
                    <div className="flex flex-wrap items-center gap-1.5 text-[color:var(--kz-color-ink-secondary)]">
                      <span>{new Date(h.ts).toLocaleString()}</span>
                      {h.unread && (
                        <span className="rounded-full bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-rose-800">
                          New
                        </span>
                      )}
                      {mapHref && (
                        <>
                          <span className="opacity-60">·</span>
                          <Link
                            to={mapHref}
                            className="inline-flex items-center gap-1 font-medium text-rose-700 underline-offset-2 hover:underline"
                          >
                            <MapPin className="size-3" />
                            View on map
                          </Link>
                        </>
                      )}
                    </div>
                    {h.message && (
                      <p className="mt-1 italic text-[color:var(--kz-color-ink-primary)]">
                        &ldquo;{h.message}&rdquo;
                      </p>
                    )}
                    {h.finder_contact && (
                      <p className="mt-0.5 flex items-center gap-1 text-[color:var(--kz-color-ink-muted)]">
                        <span>From</span>
                        <a
                          href={`tel:${h.finder_contact.replace(/[^+\d]/g, '')}`}
                          className="font-mono font-medium text-rose-700 underline-offset-2 hover:underline"
                        >
                          {h.finder_contact}
                        </a>
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function FinderMessages() {
  const queryClient = useQueryClient();
  const messages = useQuery({ queryKey: ['safeid', 'messages'], queryFn: () => safeIdApi.messages(), refetchInterval: 30_000 });
  const read = useMutation({
    mutationFn: (id: string) => safeIdApi.markRead(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['safeid', 'messages'] }),
  });
  const unread = (messages.data ?? []).filter((m) => !m.read_at);
  if (unread.length === 0) return null;
  return (
    <Card className="mt-6 border-[color:var(--kz-color-brand-primary)]/40 bg-[color:var(--kz-color-brand-primary)]/5">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base"><MessageSquare className="size-4" /> Someone found your child</CardTitle>
        <CardDescription>Messages relayed through a Safe ID scan.</CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {unread.map((m) => (
            <li key={m.id} className="flex items-start gap-3 rounded-card bg-white/70 p-3 text-sm">
              <div className="min-w-0 flex-1">
                <p>{m.body}</p>
                <p className="mt-1 text-xs text-[color:var(--kz-color-ink-muted)]">
                  {m.finder_contact ? `Contact: ${m.finder_contact} · ` : ''}{new Date(m.created_at).toLocaleString()}
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={() => read.mutate(m.id)}>Mark read</Button>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
